import {
  makeApiProvider,
  useSuspenseApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext } from 'react';
import type { IOnboardingApi } from './api';
import { OnboardingApi } from './api';
import type {
  UseMutationResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  OnboardingControllerBodyTypes,
  OnboardingControllerResultTypes,
} from '@pflegenavi/service-provider-shared/api-endpoints';

const ApiContext = createContext<IOnboardingApi | undefined>(undefined);
const { useApi: useOnboardingApi, ApiProvider: OnboardingApiProvider } =
  makeApiProvider({
    name: 'Onboarding',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new OnboardingApi(tenantId, auth, apiUrl),
  });

export { OnboardingApiProvider };

const ACCEPT_INVITATION_KEY = ['accept-invitation'];
const GET_NURSING_HOME_LIST_KEY = ['get-nursing-home-list'];

export const useAcceptInvitation = (): UseMutationResult<
  OnboardingControllerResultTypes['acceptInvitation'],
  unknown,
  OnboardingControllerBodyTypes['acceptInvitation']
> => {
  const api = useOnboardingApi();
  const queryClient = useQueryClient();
  const result = useMutation<
    OnboardingControllerResultTypes['acceptInvitation'],
    unknown,
    OnboardingControllerBodyTypes['acceptInvitation']
  >({
    mutationKey: ACCEPT_INVITATION_KEY,
    mutationFn: (data: OnboardingControllerBodyTypes['acceptInvitation']) =>
      api.acceptInvitation({
        body: data,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: GET_NURSING_HOME_LIST_KEY,
      });
    },
  });
  return result;
};

export const useGetNursingHomeList = (
  options?: Omit<
    UseSuspenseQueryOptions<
      OnboardingControllerResultTypes['getNursingHomeList'],
      unknown
    >,
    'queryKey' | 'queryFn'
  >
): UseSuspenseQueryResult<
  OnboardingControllerResultTypes['getNursingHomeList'],
  unknown
> => {
  return useSuspenseApiQuery(
    useOnboardingApi,
    GET_NURSING_HOME_LIST_KEY,
    (api) => {
      return api.getNursingHomeList();
    },
    { ...options }
  );
};
